/* eslint-disable react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';

import dayjs from 'dayjs';
import { pick, values } from 'ramda';
import { I18n } from 'src/translations/i18n';

import { withStyles } from '@material-ui/core/styles';
import {
  Button,
  Popover,
  Grid
} from '@material-ui/core';

import { MOMENT_DEFAULT_DATE_MASK, MOMENT_SHORT_DATE_MASK } from 'src/constants/dayjs';

import Utils from 'src/utils';
import FormControlLabel from '../../Form/FormControlLabel';
import DateRangePicker from './Picker';

const periodsObject = pick(['today', 'lastDay', 'thisWeek', 'lastWeek', 'thisMonth', 'lastMonth'], Utils.periods.getPeriods());
const defaultPeriods = values(periodsObject);

const styles = (theme) => ({
  paperRoot: {
    overflowX: 'auto',
    color: '#666'
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'column'
  },
  root: {
    display: 'flex'
  },
  paper: {
    marginRight: theme.spacing(1)
  },
  activeTitle: {
    fontWeight: 'bold'
  },
  popupContainer: {
    zIndex: 1000,
    width: 660,
    maxWidth: 800
  },
  input: {
    cursor: 'pointer'
  },
  button: {
    height: 34,
    '&>span': {
      textTransform: 'none'
    }
  }
});

class DropdownDateRangePicker extends React.Component {
  constructor() {
    super();
    this.state = {
      open: false
    };
  }

  handleToggle = () => {
    this.setState(({ open }) => ({ open: !open }));
    this.props.activate();
  };

  handleClose = () => {
    this.setState({ open: false });
  };

  onSubmit = (value) => {
    this.props.onChange(value);
    this.handleClose();
  };

  render() {
    const {
      classes, value, isActive,
      index, label, mouseInKey,
      isRequired, containerClassName, buttonClassName,
      ...pickerProps
    } = this.props;
    const { startDate, endDate } = value;
    const { open } = this.state;
    const from = startDate ? dayjs(startDate, MOMENT_SHORT_DATE_MASK).format(MOMENT_SHORT_DATE_MASK) : '';
    const to = endDate ? dayjs(endDate, MOMENT_SHORT_DATE_MASK).format(MOMENT_SHORT_DATE_MASK) : '';

    let title = I18n.t('NotSelected');
    if (from && to) title = `${from}-${to}`;
    else if (from && !to) title = `${I18n.t('From')} ${from}`;
    else if (!from && to) title = `${I18n.t('To')} ${to}`;
    const key = `DateRangePicker-${title}-${index}`;

    return (
      <Grid item className={`${classes.inputContainer} ${containerClassName}`}>
        { label && (
          <FormControlLabel
            title={I18n.t(label)}
            mouseInKey={mouseInKey}
            isRequired={isRequired}
          />
        )}

        <Button
          variant="outlined"
          className={`${classes.button} ${isActive ? classes.activeTitle : ''} ${buttonClassName}`}
          onClick={this.handleToggle}
          id={key}
          ref={(node) => { this.anchorEl = node; }}
          readOnly
          aria-haspopup="true"
          aria-owns={open ? 'menu-list-grow' : null}
        >
          {title}
        </Button>

        <Popover
          onClose={this.handleClose}
          open={open}
          anchorEl={this.anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          PaperProps={{ className: classes.paperRoot }}
        >
          <DateRangePicker
            {...pickerProps}
            key={key}
            onSubmit={this.onSubmit}
            onClose={this.handleClose}
            value={value}
            format={MOMENT_SHORT_DATE_MASK}
          />
        </Popover>
      </Grid>
    );
  }
}

DropdownDateRangePicker.propTypes = {
  value: PropTypes.shape({
    startDate: PropTypes.any, // null or dayjs
    endDate: PropTypes.any // null or dayjs
  }),
  onChange: PropTypes.func.isRequired,
  index: PropTypes.number,
  activate: PropTypes.func,
  isActive: PropTypes.bool,
  format: PropTypes.string,
  classes: PropTypes.object,
  periods: PropTypes.array,
  label: PropTypes.string,
  mouseInKey: PropTypes.string,
  containerClassName: PropTypes.string,
  buttonClassName: PropTypes.string,
  isRequired: PropTypes.bool,
  showPresets: PropTypes.bool,
  allowEmpty: PropTypes.bool
};

DropdownDateRangePicker.defaultProps = {
  value: {
    startDate: dayjs(),
    endDate: dayjs()
  },
  index: 0,
  activate: () => null,
  isActive: false,
  periods: defaultPeriods,
  format: MOMENT_DEFAULT_DATE_MASK,
  classes: {},
  label: '',
  mouseInKey: '',
  containerClassName: '',
  buttonClassName: '',
  isRequired: false,
  showPresets: true,
  allowEmpty: false
};

export default withStyles(styles)(DropdownDateRangePicker);
